import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, TextField } from '@mui/material';
import { useAuthStore } from '../../stores/authStore';
import { ApiError } from '../../lib/apiClient';
import PasswordField from '../../components/PasswordField';
import AuthLayout from './AuthLayout';

/** Lien d'invitation : /activation#c=<mot de passe temporaire>&u=<identifiant>. */
function readInvitation(): { code: string; username: string } | null {
  const m = window.location.hash.match(/^#c=([A-Za-z0-9_-]+)&u=(.+)$/);
  if (!m) return null;
  try {
    return { code: m[1], username: decodeURIComponent(m[2]) };
  } catch {
    return { code: m[1], username: m[2] };
  }
}

export default function ActivationPage() {
  const activate = useAuthStore((s) => s.activate);
  const navigate = useNavigate();
  // Lu une fois puis retiré de l'URL (historique, partage d'écran).
  const [invitation] = useState(readInvitation);
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!invitation || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await activate(invitation.username, invitation.code, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'INVALID_CREDENTIALS'
          ? 'Lien invalide ou expiré.'
          : err instanceof ApiError
            ? err.message
            : 'Activation impossible.'
      );
      setBusy(false);
    }
  };

  if (!invitation) {
    return (
      <AuthLayout title="Activation">
        <Alert severity="error" sx={{ mb: 2 }}>
          Lien invalide ou expiré.
        </Alert>
        <Button component={RouterLink} to="/login" variant="outlined" fullWidth>
          Connexion
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Activation du compte">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {invitation.username.includes('@') && (
          <TextField label="E-mail" value={invitation.username} disabled fullWidth autoComplete="username" />
        )}
        <PasswordField
          label="Nouveau mot de passe"
          autoComplete="new-password"
          autoFocus
          required
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label="Confirmation"
          autoComplete="new-password"
          required
          fullWidth
          value={confirm}
          error={mismatch}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" variant="contained" size="large" disabled={busy || mismatch} sx={{ py: 1.25, mt: 1 }}>
          Activer
        </Button>
      </Box>
    </AuthLayout>
  );
}
